'use client';

import './share-buttons.scss';

import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { getPublicConfig } from '@sola/config/lib/getters.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';

type ShareButtonsProp = {
  shareUrl?: string;
  shareTitle?: string;
  shareSummary?: string;
};
export const ShareButtons: React.FC<ShareButtonsProp> = ({
  shareUrl,
  shareTitle,
  shareSummary,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { NEXT_PUBLIC_BASE_URL_DOTCOM } = getPublicConfig();
  const pathname = usePathname();

  const shareLink =
    shareUrl ||
    `${NEXT_PUBLIC_BASE_URL_DOTCOM}${pathname}` ||
    `${NEXT_PUBLIC_BASE_URL_DOTCOM}`;

  const [copyButtonText, setCopyButtonText] = useState<string>(
    t('skippy.socialMedia.copy'),
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch (errors) {
      setCopyButtonText(String(errors));
      setTimeout(() => {
        setCopyButtonText(t('skippy.socialMedia.copy'));
      }, 1000);
      return;
    } finally {
      (
        document.querySelector('input[name=articleURL]') as HTMLInputElement
      ).select();
      setCopyButtonText(t('skippy.socialMedia.copied'));
      setTimeout(() => {
        setCopyButtonText(t('skippy.socialMedia.copy'));
      }, 1000);
    }
  };

  return (
    <div className="ShareButtons">
      <Dialog
        open={open}
        onOpenChange={async (tryToOpen) => {
          if (tryToOpen) {
            const nativeShareData = {
              title: shareTitle || document.title,
              text: shareSummary || '',
              url: shareLink,
            };
            if (
              navigator.maxTouchPoints > 1 &&
              navigator.canShare &&
              navigator.canShare(nativeShareData)
            ) {
              trackClick('native share sheet', shareLink);
              try {
                await navigator.share(nativeShareData);
              } catch (error) {
                if ((error as DOMException).name !== 'AbortError') {
                  trackClick('custom share sheet', shareLink);
                  setOpen(true);
                } else {
                  setOpen(false);
                }
              }
            } else {
              trackClick('custom share sheet', shareLink);
              setOpen(true);
            }
          } else {
            setOpen(false);
          }
        }}
      >
        <DialogTrigger>
          <ButtonStyle
            size="md"
            iconBefore={<ArkIcon iconName="share-02" />}
            purpose="neutral"
            variant="secondary"
          >
            <button type="button">{t('core.actions.share')}</button>
          </ButtonStyle>
        </DialogTrigger>
        <DialogContent
          title={t('skippy.socialMedia.share')}
          style={{ textAlign: 'center' }}
        >
          <div className="ShareButtons__modal flex flex-col">
            <div className="flex flex-row gap-comfy-lg mb-comfy-xl align-center">
              <button
                type="button"
                onClick={async () => {
                  trackClick('facebook - share', shareLink);
                  const facebookPost = `https://www.facebook.com/sharer/sharer.php?u=${shareLink}`;
                  window
                    .open(facebookPost, '_blank', 'noopener,noreferrer')
                    ?.focus();
                }}
              >
                <ArkIcon
                  iconName="facebook"
                  alt={t('skippy.socialMedia.facebook')}
                />
              </button>
              <button
                type="button"
                onClick={() => {
                  trackClick('twitter - share', shareLink);
                  const twitterTitle = encodeURIComponent(
                    shareTitle || document.title,
                  );
                  const tweet = `https://twitter.com/intent/tweet?url=${shareLink}&text=${twitterTitle}&via=compassion`;
                  window.open(tweet, '_blank', 'noopener,noreferrer')?.focus();
                }}
              >
                <ArkIcon
                  iconName="twitter"
                  alt={t('skippy.socialMedia.twitter')}
                />
              </button>
              <button
                type="button"
                onClick={() => {
                  trackClick('linkedin - share', shareLink);
                  const linkedinPost = `https://www.linkedin.com/cws/share?url=${shareLink}`;
                  window
                    .open(linkedinPost, '_blank', 'noopener,noreferrer')
                    ?.focus();
                }}
              >
                <ArkIcon
                  iconName="linkedin"
                  alt={t('skippy.socialMedia.linkedin')}
                />
              </button>
              <button
                type="button"
                onClick={() => {
                  trackClick('email - share', shareLink);
                  const emailSubject = encodeURIComponent(
                    shareTitle || document.title,
                  );
                  const emailDescription = encodeURIComponent(
                    shareSummary || '',
                  );
                  const emailBody = `${emailDescription}%20${shareLink}`;
                  const email = `mailto:?subject=${emailSubject}&body=${emailBody}`;
                  window.open(email, '_blank', 'noopener,noreferrer')?.focus();
                }}
              >
                <ArkIcon
                  iconName="mail-01"
                  alt={t('skippy.socialMedia.email')}
                />
              </button>
            </div>
            <div className="flex flex-row align-center gap-snug-md pb-comfy-lg">
              <div className="InputField">
                <input
                  name="articleURL"
                  className="form-control"
                  aria-label={t('skippy.socialMedia.copy')}
                  type="text"
                  size={255}
                  value={shareLink}
                  readOnly
                  onClick={async () => {
                    copyLink();
                  }}
                />
              </div>
              <ButtonStyle size="md">
                <button
                  type="button"
                  onClick={async () => {
                    copyLink();
                  }}
                >
                  {copyButtonText}
                </button>
              </ButtonStyle>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
